import { useState, useEffect } from 'react';
import { useUser } from '../../auth';
import { CreateFormState, SelectedModel } from './create.types';
import { SUPPORTED_CURRENCIES, currencySymbol, formatMoney, formatMoneyMajor, normalizeCurrency } from '../../shared/money';
import { usePlatformFee } from '../../shared/usePlatformFee';
import { GuaranteeCardModal, usePaymentMethodStatus } from './GuaranteeCardModal';
import { buildRevenueSplitChartState, formatSplitPercent, moneyInputPaddingLeft } from './modelPaymentViewState';
import type { Profile, Room, Invite } from '@modelcast/types';
import { authHeaders } from '../../shared/authHeaders';

const JOB_AD_TEXT_ERROR = 'Please provide details of the session to potential applicants';

interface ModelPaymentStepProps {
  formState: CreateFormState;
  onUpdate: (updates: Partial<CreateFormState>) => void;
  onNext: (options?: { requireModelInviteEmails?: boolean }) => void | Promise<void>;
  onBack: () => void;
  isSubmitting: boolean;
  getToken: () => Promise<string | null>;
  roomCode: string | null;
  roomId: string | null;
  room: Room | null;
  onRefreshRoom: () => void;
}

// Helper to validate email format
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function clampPercent(value: number | string | null | undefined): number {
  const numericValue = Number(value ?? 0);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.max(0, Math.min(100, Math.round(numericValue ?? 0)));
}

function getSplitTotal(models: SelectedModel[]): number {
  return models.reduce((sum, model) => sum + clampPercent(model.splitPercent), 0);
}

function getGuaranteeTotal(models: SelectedModel[]): number | null {
  const total = models.reduce((sum, model) => (
    sum + (model.source === 'organizer_self' ? 0 : model.guarantee ?? 0)
  ), 0);
  return total || null;
}

function getEvenSplitRecipientCount(models: SelectedModel[]): number {
  if (models.length === 0) return 0;
  const organizerIsModel = models.some(model => model.source === 'organizer_self');
  return models.length + (organizerIsModel ? 0 : 1);
}

function normalizeSelectedModelSplits(models: SelectedModel[]): SelectedModel[] {
  let remaining = 100;
  let organizerSplitApplied = false;

  const modelsWithClampedSplits = models.map((model) => {
    if (model.source === 'organizer_self') return model;

    const splitPercent = Math.min(clampPercent(model.splitPercent), remaining);
    remaining -= splitPercent;

    return splitPercent === model.splitPercent
      ? model
      : { ...model, splitPercent };
  });

  return modelsWithClampedSplits.map((model) => {
    if (model.source !== 'organizer_self') return model;

    const splitPercent = organizerSplitApplied ? 0 : remaining;
    organizerSplitApplied = true;

    return splitPercent === model.splitPercent
      ? model
      : { ...model, splitPercent };
  });
}

function selectedModelSplitsChanged(a: SelectedModel[], b: SelectedModel[]): boolean {
  return a.length !== b.length || a.some((model, index) => model.splitPercent !== b[index]?.splitPercent);
}

function hasMetricSystemSplitApology(splitValues: number[]): boolean {
  return splitValues.filter((value) => value === 34).length === 1 &&
    splitValues.filter((value) => value === 33).length === splitValues.length - 1;
}

function isJobApplicantRow(model: SelectedModel): boolean {
  return model.source === 'application' && !model.profileId && !model.email;
}

function hasModelInviteBeenSent(model: SelectedModel, invite?: Invite) {
  return Boolean(invite?.emailSentAt ?? model.emailSentAt) ||
    model.status === 'invited' ||
    model.status === 'accepted' ||
    model.status === 'declined';
}

function getModelOptionForRows(models: SelectedModel[]): CreateFormState['modelOption'] {
  if (models.length === 0) return 'none';
  if (models.some(isJobApplicantRow)) return 'ad';
  if (models.length === 1 && models[0]?.source === 'organizer_self') return 'self';
  return 'invite';
}

export function ModelPaymentStep({
  formState,
  onUpdate,
  onNext,
  onBack,
  isSubmitting,
  getToken,
  roomCode,
  roomId,
  room,
  onRefreshRoom,
}: ModelPaymentStepProps) {
  const { user } = useUser();
  const { feePercent: platformFeePercent } = usePlatformFee();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Profile[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [modelError, setModelError] = useState<string | null>(null);
  const [modelInvites, setModelInvites] = useState<Invite[]>([]);
  const [isLoadingInvite, setIsLoadingInvite] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [searchComplete, setSearchComplete] = useState(false);

  const { hasPaymentMethod, paymentMethod, isLoading: isCheckingPayment, refresh: refreshPaymentStatus } = usePaymentMethodStatus(getToken);
  const currentUserEmails = new Set(
    user?.emailAddresses.map((email) => normalizeEmail(email.emailAddress)) ?? []
  );
  const isOwnEmail = (email: string) => currentUserEmails.has(normalizeEmail(email));
  const isOwnProfile = (profile: Profile) => profile.authUserId === user?.id || profile.userId === user?.id;
  const isSearchingOwnEmail = isValidEmail(searchQuery) && isOwnEmail(searchQuery);

  // Fetch model invite when room code is available.
  useEffect(() => {
    if (!roomCode) {
      setModelInvites([]);
      return;
    }

    async function fetchModelInvites() {
      setIsLoadingInvite(true);
      try {
        const token = await getToken();
        const response = await fetch(`/api/rooms/${roomCode}/invites`, {
          headers: authHeaders(token),
        });
        if (response.ok) {
          const data = await response.json();
          const invites = ((data.invites || []) as Invite[]).filter((i) => i.role === 'model');
          setModelInvites(invites);
          if (invites.length > 0 && formState.selectedModels.length === 0) {
            const selectedModels = invites.map((invite) => ({
              profileId: invite.profileId,
              email: invite.profileId ? null : invite.email,
              displayName: invite.name || invite.email || 'Invited model',
              photoUrl: null,
              source: 'invite' as const,
              splitPercent: formState.modelSplitPercent,
              guarantee: 0,
              inviteId: invite.id,
              emailSentAt: invite.emailSentAt,
              rsvpStatus: invite.rsvpStatus,
            }));
            onUpdate({
              modelOption: getModelOptionForRows(selectedModels),
              selectedModel: {
                profileId: invites[0].profileId,
                email: invites[0].profileId ? null : invites[0].email,
                displayName: invites[0].name || invites[0].email || 'Invited model',
                photoUrl: null,
                source: 'invite',
                splitPercent: formState.modelSplitPercent,
                guarantee: 0,
                inviteId: invites[0].id,
                emailSentAt: invites[0].emailSentAt,
                rsvpStatus: invites[0].rsvpStatus,
              },
              selectedModels,
            });
          }
        }
      } catch (error) {
        console.error('Error fetching model invite:', error);
      } finally {
        setIsLoadingInvite(false);
      }
    }

    fetchModelInvites();
  }, [roomCode, formState.selectedModels.length, room?.modelProfileId, getToken]);

  const isPublished = room?.isPublished ?? false;
  const selectedOrganizerModel = formState.selectedModels.find(model => model.source === 'organizer_self') ?? null;
  const organizerIsOnlyModel = selectedOrganizerModel !== null && formState.selectedModels.length === 1;
  const isEvenSplit = formState.revenueSplitMode === 'even';
  // No guarantee needed when you're the model yourself or there's no model
  const externalSelectedModels = formState.selectedModels.filter(model => model.source !== 'organizer_self');
  const totalExternalModelSplit = externalSelectedModels.reduce((sum, model) => sum + clampPercent(model.splitPercent), 0);
  const organizerRemainder = Math.max(0, 100 - totalExternalModelSplit);
  const visibleRevenueSplitTotal = formState.selectedModels.length > 0
    ? isEvenSplit
      ? 100
      : totalExternalModelSplit + (selectedOrganizerModel ? clampPercent(selectedOrganizerModel.splitPercent) : organizerRemainder)
    : 100;
  const visibleSplitValues = [
    ...formState.selectedModels.map((model) => (
      model.source === 'organizer_self' ? organizerRemainder : clampPercent(model.splitPercent)
    )),
    ...(!selectedOrganizerModel && formState.selectedModels.length > 0 ? [organizerRemainder] : []),
  ];
  const showMetricSystemSplitApology = !isEvenSplit && visibleRevenueSplitTotal === 100 &&
    hasMetricSystemSplitApology(visibleSplitValues);
  const totalSelectedModelGuarantee = externalSelectedModels.reduce((sum, model) => sum + (model.guarantee ?? 0), 0);
  const activeGuaranteeAmount = formState.selectedModels.length > 0
    ? totalSelectedModelGuarantee
    : formState.modelGuarantee ?? 0;
  const hasModelRowsForGuarantee = externalSelectedModels.length > 0;
  const hasGuarantee = formState.pricingType !== 'free' && activeGuaranteeAmount > 0 && hasModelRowsForGuarantee;
  const showPaidModelTerms = formState.pricingType !== 'free';
  const showPayoutModeToggle = showPaidModelTerms && formState.selectedModels.length > 0 && !organizerIsOnlyModel;
  const hasJobAdRows = formState.selectedModels.some(isJobApplicantRow);
  const hasKnownModelInviteRows = formState.selectedModels.some((model) => (
    model.source !== 'organizer_self' && !isJobApplicantRow(model)
  ));
  const moneyInputStyle = { paddingLeft: moneyInputPaddingLeft(formState.currency) };
  const currencyPrefix = currencySymbol(formState.currency);
  const { segments: splitChartSegments, gradient: splitChartGradient, accessibleLabel: splitChartAccessibleLabel } = buildRevenueSplitChartState({
    selectedModels: formState.selectedModels,
    showPaidModelTerms,
    isEvenSplit,
    organizerRemainder,
    selectedOrganizerModel,
  });

  const updateSelectedModels = (models: SelectedModel[], revenueSplitMode = formState.revenueSplitMode) => {
    const normalizedModels = normalizeSelectedModelSplits(models);
    onUpdate({
      modelOption: getModelOptionForRows(normalizedModels),
      selectedModels: normalizedModels,
      selectedModel: normalizedModels[0] ?? null,
      modelSplitPercent: getSplitTotal(normalizedModels),
      modelGuarantee: getGuaranteeTotal(normalizedModels),
      revenueSplitMode,
    });
  };

  const getMaxSplitForModel = (target: SelectedModel) => {
    const otherSplitTotal = formState.selectedModels.reduce((sum, model) => {
      if (model === target || model.source === 'organizer_self') return sum;
      return sum + clampPercent(model.splitPercent);
    }, 0);
    return Math.max(0, 100 - otherSplitTotal);
  };

  const setPayoutMode = (revenueSplitMode: 'even' | 'manual') => {
    if (revenueSplitMode === 'even' && getEvenSplitRecipientCount(formState.selectedModels) === 0) return;
    updateSelectedModels(formState.selectedModels, revenueSplitMode);
    setModelError(null);
  };

  useEffect(() => {
    if (formState.selectedModels.length === 0) return;

    const normalizedModels = normalizeSelectedModelSplits(formState.selectedModels);
    if (!selectedModelSplitsChanged(normalizedModels, formState.selectedModels)) return;

    onUpdate({
      modelOption: getModelOptionForRows(normalizedModels),
      selectedModels: normalizedModels,
      selectedModel: normalizedModels[0] ?? null,
      modelSplitPercent: getSplitTotal(normalizedModels),
      modelGuarantee: getGuaranteeTotal(normalizedModels),
    });
  }, [formState.selectedModels, onUpdate]);

  // Search for models
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSearchComplete(false);
      return;
    }

    setSearchComplete(false);
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const token = await getToken();
        const response = await fetch(`/api/profiles?search=${encodeURIComponent(searchQuery)}&isModel=true`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          const profiles = (data.profiles || []) as Profile[];
          setSearchResults(profiles.filter((profile) => !isOwnProfile(profile)));
        }
      } catch (error) {
        console.error('Error searching models:', error);
      } finally {
        setIsSearching(false);
        setSearchComplete(true);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, getToken, user?.id]);

  const findInviteForModel = (model: SelectedModel) => {
    return modelInvites.find((invite) => (
      (model.profileId && invite.profileId === model.profileId) ||
      (model.email && invite.email?.toLowerCase() === model.email.toLowerCase()) ||
      (model.inviteId && invite.id === model.inviteId)
    ));
  };
  const hasPendingModelInvitation = formState.selectedModels.some((model) => (
    model.source !== 'organizer_self' &&
    !isJobApplicantRow(model) &&
    !hasModelInviteBeenSent(model, findInviteForModel(model))
  ));

  const upsertSelectedModel = (model: SelectedModel) => {
    const existingIndex = formState.selectedModels.findIndex((existing) => (
      (model.profileId && existing.profileId === model.profileId) ||
      (model.email && existing.email?.toLowerCase() === model.email.toLowerCase()) ||
      (model.source === 'organizer_self' && existing.source === 'organizer_self')
    ));
    const isAddingFirstExternalModel = existingIndex < 0 &&
      formState.selectedModels.length === 0 &&
      model.source !== 'organizer_self';
    const modelWithDefaults = isAddingFirstExternalModel
      ? { ...model, splitPercent: formState.isTutored ? 50 : 75 }
      : model;
    const revenueSplitMode = isAddingFirstExternalModel
      ? formState.isTutored ? 'even' : 'manual'
      : formState.revenueSplitMode;
    const nextModels = existingIndex >= 0
      ? formState.selectedModels.map((existing, index) => index === existingIndex ? modelWithDefaults : existing)
      : [...formState.selectedModels, modelWithDefaults];

    const normalizedModels = normalizeSelectedModelSplits(nextModels);
    onUpdate({
      modelOption: getModelOptionForRows(normalizedModels),
      selectedModels: normalizedModels,
      selectedModel: normalizedModels[0] ?? null,
      modelSplitPercent: getSplitTotal(normalizedModels),
      modelGuarantee: getGuaranteeTotal(normalizedModels),
      revenueSplitMode,
    });
  };

  const handleSelectModel = (profile: Profile) => {
    if (isOwnProfile(profile)) return;

    const selectedModel: SelectedModel = {
      profileId: profile.id,
      email: null,
      displayName: profile.displayName,
      photoUrl: profile.photos?.[0]?.url || null,
      source: 'invite',
      splitPercent: formState.modelSplitPercent,
      guarantee: 0,
    };
    upsertSelectedModel(selectedModel);
    setModelError(null);
    setSearchQuery('');
    setSearchComplete(false);
  };

  const handleInviteByEmail = (email: string) => {
    if (isOwnEmail(email)) return;

    const selectedModel: SelectedModel = {
      profileId: null, // No profile - email-only invite
      email,
      displayName: email, // Use email as display name for now
      photoUrl: null,
      source: 'invite',
      splitPercent: formState.modelSplitPercent,
      guarantee: 0,
    };
    upsertSelectedModel(selectedModel);
    setModelError(null);
    setSearchQuery('');
    setSearchComplete(false);
  };

  const addJobApplicantRow = () => {
    const nextJobRowNumber = formState.selectedModels
      .filter(isJobApplicantRow)
      .reduce((max, model) => {
        const match = model.displayName.match(/^Applicant (\d+)$/);
        return Math.max(max, match ? Number(match[1]) : 0);
      }, 0) + 1;
    const selectedModel: SelectedModel = {
      profileId: null,
      email: null,
      displayName: `Applicant ${nextJobRowNumber}`,
      photoUrl: null,
      source: 'application',
      status: 'pending',
      splitPercent: formState.modelSplitPercent,
      guarantee: 0,
    };
    upsertSelectedModel(selectedModel);
    setModelError(null);
    setSearchQuery('');
    setSearchComplete(false);
  };

  const addOrganizerAsModel = () => {
    upsertSelectedModel({
      profileId: null,
      email: null,
      displayName: 'You',
      photoUrl: null,
      source: 'organizer_self',
      splitPercent: formState.modelSplitPercent,
      guarantee: null,
    });
    setModelError(null);
  };

  const clearSelectedModel = (model?: SelectedModel) => {
    setModelError(null);
    const nextModels = model
      ? formState.selectedModels.filter((selected) => selected !== model)
      : [];
    const normalizedModels = normalizeSelectedModelSplits(nextModels);
    onUpdate({
      modelOption: getModelOptionForRows(normalizedModels),
      selectedModels: normalizedModels,
      selectedModel: normalizedModels[0] ?? null,
      modelSplitPercent: getSplitTotal(normalizedModels),
      modelGuarantee: getGuaranteeTotal(normalizedModels),
    });
    setSearchQuery('');
    setSearchComplete(false);
  };

  const updateSelectedModelTerms = (
    target: SelectedModel,
    updates: {
      splitPercent?: number | string;
      guarantee?: number | null;
    }
  ) => {
    const nextModels = formState.selectedModels.map((model) => {
      if (model !== target) return model;

      const nextModel: SelectedModel = {
        ...model,
        ...(updates.guarantee !== undefined ? { guarantee: updates.guarantee } : {}),
      };
      if (updates.splitPercent !== undefined && model.source !== 'organizer_self') {
        nextModel.splitPercent = Math.min(
          clampPercent(updates.splitPercent),
          getMaxSplitForModel(model)
        );
      }

      return nextModel;
    });
    updateSelectedModels(
      nextModels,
      updates.splitPercent !== undefined ? 'manual' : formState.revenueSplitMode
    );
  };

  const handleClearSelectedModel = async (model: SelectedModel) => {
    const invite = findInviteForModel(model);
    if (!invite) {
      clearSelectedModel(model);
      return;
    }

    if (!roomCode) return;

    const modelName = model.displayName || 'this model';
    const confirmed = window.confirm(
      `Are you sure you want to withdraw this invitation?\n\nWe will email ${modelName} to tell them the invitation has been withdrawn.`
    );
    if (!confirmed) return;

    setIsWithdrawing(true);
    try {
      const token = await getToken();
      const response = await fetch(`/api/rooms/${roomCode}/invites/${invite.id}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });

      if (response.ok) {
        setModelInvites((current) => current.filter((item) => item.id !== invite.id));
        clearSelectedModel(model);
      } else {
        alert('Failed to withdraw invitation. Please try again.');
      }
    } catch (error) {
      console.error('Error withdrawing invitation:', error);
      alert('Failed to withdraw invitation. Please try again.');
    } finally {
      setIsWithdrawing(false);
    }
  };

  // Calculate payouts based on ticket price and slider
  const ticketPrice = formState.ticketPrice || 0;
  const simulatorTickets = 10; // Default for simulator
  const [simulatorValue, setSimulatorValue] = useState(simulatorTickets);

  const getSimulatorRecipients = () => {
    const modelRecipients = formState.selectedModels.map((model, index) => {
      const isOrganizerModel = model.source === 'organizer_self';
      return {
        key: model.id ?? `${model.source ?? 'model'}:${model.profileId ?? model.email ?? model.displayName}:${index}`,
        label: model.displayName || (isOrganizerModel ? 'You' : `Model ${index + 1}`),
        role: isOrganizerModel
          ? 'Organizer / model'
          : isJobApplicantRow(model)
            ? 'Applicant'
            : 'Model',
        splitPercent: clampPercent(isOrganizerModel ? organizerRemainder : model.splitPercent),
        guaranteeCents: isOrganizerModel ? 0 : Math.round((model.guarantee ?? 0) * 100),
        isOrganizer: isOrganizerModel,
      };
    });

    if (!selectedOrganizerModel && formState.selectedModels.length > 0) {
      modelRecipients.push({
        key: 'organizer-remainder',
        label: 'You',
        role: 'Organizer',
        splitPercent: organizerRemainder,
        guaranteeCents: 0,
        isOrganizer: true,
      });
    }

    return modelRecipients;
  };

  const calculatePayouts = (tickets: number) => {
    const grossRevenueCents = Math.round(ticketPrice * 100) * tickets;
    const platformFeeCents = Math.round(grossRevenueCents * (platformFeePercent / 100));
    const netRevenueCents = grossRevenueCents - platformFeeCents;
    const recipients = getSimulatorRecipients();
    const separateOrganizerRecipient = recipients.find((recipient) => recipient.role === 'Organizer') ?? null;
    const baseRows = isEvenSplit
      ? (() => {
        const recipientCount = Math.max(1, recipients.length);
        const equalShareCents = Math.floor(netRevenueCents / recipientCount);
        const remainderRecipientIndex = Math.max(0, recipients.findIndex((recipient) => recipient.isOrganizer));
        return recipients.map((recipient, index) => ({
          ...recipient,
          splitLabel: recipients.length === 1 ? '100% of net' : 'Even share',
          basePayoutCents: index === remainderRecipientIndex
            ? netRevenueCents - (equalShareCents * (recipientCount - 1))
            : equalShareCents,
        }));
      })()
      : (() => {
        const modelRecipients = recipients.filter((recipient) => recipient.role !== 'Organizer');
        const modelBasePayouts = modelRecipients.map((recipient) => ({
          ...recipient,
          splitLabel: `${recipient.splitPercent}% of net`,
          basePayoutCents: Math.round(netRevenueCents * (recipient.splitPercent / 100)),
        }));
        const baseModelPayoutCents = modelBasePayouts.reduce((sum, recipient) => sum + recipient.basePayoutCents, 0);
        const organizerPayoutCents = Math.max(0, netRevenueCents - baseModelPayoutCents);

        return separateOrganizerRecipient
          ? [
            ...modelBasePayouts,
            {
              ...separateOrganizerRecipient,
              splitLabel: `${separateOrganizerRecipient.splitPercent}% of net`,
              basePayoutCents: organizerPayoutCents,
            },
          ]
          : modelBasePayouts;
      })();

    let separateOrganizerPayoutCents = baseRows.find((row) => row.role === 'Organizer')?.basePayoutCents ?? 0;
    let guaranteeChargedCents = 0;

    const payoutRows = baseRows.map((recipient) => {
      if (recipient.role === 'Organizer') {
        return {
          ...recipient,
          payoutCents: 0,
          guaranteeTopUpCents: 0,
          guaranteeChargedCents: 0,
        };
      }

      if (recipient.guaranteeCents <= recipient.basePayoutCents) {
        return {
          ...recipient,
          payoutCents: recipient.basePayoutCents,
          guaranteeTopUpCents: 0,
          guaranteeChargedCents: 0,
        };
      }

      const guaranteeTopUpCents = recipient.guaranteeCents - recipient.basePayoutCents;
      const reallocatedFromOrganizerCents = Math.min(separateOrganizerPayoutCents, guaranteeTopUpCents);
      separateOrganizerPayoutCents -= reallocatedFromOrganizerCents;
      const chargedCents = guaranteeTopUpCents - reallocatedFromOrganizerCents;
      guaranteeChargedCents += chargedCents;

      return {
        ...recipient,
        payoutCents: recipient.basePayoutCents + reallocatedFromOrganizerCents + chargedCents,
        guaranteeTopUpCents,
        guaranteeChargedCents: chargedCents,
      };
    }).map((row) => (
      row.role === 'Organizer'
        ? { ...row, payoutCents: separateOrganizerPayoutCents }
        : row
    ));

    const centsToDollars = (cents: number) => cents / 100;

    return {
      grossRevenue: centsToDollars(grossRevenueCents),
      platformFee: centsToDollars(platformFeeCents),
      netRevenue: centsToDollars(netRevenueCents),
      guaranteeCharged: centsToDollars(guaranteeChargedCents),
      payoutRows: payoutRows.map((row) => ({
        ...row,
        basePayout: centsToDollars(row.basePayoutCents),
        payout: centsToDollars(row.payoutCents),
        guarantee: centsToDollars(row.guaranteeCents),
        guaranteeTopUp: centsToDollars(row.guaranteeTopUpCents),
        guaranteeCharged: centsToDollars(row.guaranteeChargedCents),
      })),
    };
  };

  const maxTickets = formState.maxAttendees || 50;
  const clampedSimulatorValue = Math.min(simulatorValue, maxTickets);
  const payouts = calculatePayouts(clampedSimulatorValue);

  const handlePublish = async () => {
    // If there's a guarantee and no payment method, show the card modal
    if (hasGuarantee && !hasPaymentMethod) {
      setShowCardModal(true);
      return;
    }

    await doPublish();
  };

  const doPublish = async () => {
    if (!roomCode || !roomId) return;

    setIsPublishing(true);
    setPublishError(null);

    try {
      const token = await getToken();

      // If there's a guarantee, create the hold first
      if (hasGuarantee) {
        const guaranteeResponse = await fetch('/api/stripe/guarantee-hold', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            roomId,
            roomCode,
            amountCents: Math.round(activeGuaranteeAmount * 100),
          }),
        });

        if (!guaranteeResponse.ok) {
          const errorData = await guaranteeResponse.json();
          throw new Error(errorData.error || 'Failed to create guarantee hold');
        }

        const holdResult = await guaranteeResponse.json();
        if (holdResult.status !== 'requires_capture') {
          throw new Error('Card authorization failed. Please check your payment method.');
        }
      }

      // Now publish the event
      const publishResponse = await fetch(`/api/rooms/${roomCode}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ isPublished: true }),
      });

      if (!publishResponse.ok) {
        throw new Error('Failed to publish event');
      }

      onRefreshRoom();
      // After publishing, proceed to invites step
      onNext();
    } catch (error) {
      console.error('Error publishing:', error);
      setPublishError(error instanceof Error ? error.message : 'Failed to publish');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCardSuccess = () => {
    setShowCardModal(false);
    refreshPaymentStatus();
    // Card saved - user must click Next to proceed
  };

  const handleContinue = () => {
    if (
      formState.pricingType !== 'free' &&
      formState.selectedModels.length > 0 &&
      visibleRevenueSplitTotal !== 100
    ) {
      setModelError('Revenue split must add up to 100% before continuing.');
      return;
    }

    if (hasJobAdRows && !formState.modelAdText.trim()) {
      setModelError(JOB_AD_TEXT_ERROR);
      return;
    }

    setModelError(null);
    setPublishError(null);
    void onNext({ requireModelInviteEmails: hasPendingModelInvitation });
  };

  return (
    <div className="flex-1 flex justify-center bg-bg">
      <div className="w-full max-w-[640px] py-8 sm:py-10 lg:py-[72px] flex flex-col gap-7">
        <div>
          <h1 className="type-title-page text-text">
            Pricing & Model
          </h1>
          <p className="type-body text-text-dim mt-3.5">
            Set up ticket pricing and select a model for your event.
          </p>
        </div>

        {/* Pricing Section */}
        <div className="flex flex-col gap-4">
          <h2 className="type-title-card text-text">Pricing</h2>

          {formState.pricingType !== 'free' && (
            <div className="flex flex-col gap-2">
              <label htmlFor="event-currency" className="type-body-sm text-text-dim">Currency</label>
              <select
                id="event-currency"
                value={normalizeCurrency(formState.currency)}
                onChange={(e) => onUpdate({ currency: e.target.value })}
                className="input w-full sm:w-64"
              >
                {SUPPORTED_CURRENCIES.map((entry) => (
                  <option key={entry.code} value={entry.code}>{entry.label}</option>
                ))}
              </select>
              <p className="type-caption text-text-faint">
                Tickets are charged and paid out in this currency.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-2.5">
            <RadioOption
              selected={formState.pricingType === 'free'}
              onClick={() => onUpdate({
                pricingType: 'free',
                ticketPrice: 0,
                suggestedMinimum: null,
                modelGuarantee: null,
              })}
              label="Free"
            />
            <RadioOption
              selected={formState.pricingType === 'fixed'}
              onClick={() => onUpdate({
                pricingType: 'fixed',
                ticketPrice: formState.ticketPrice && formState.ticketPrice > 0 ? formState.ticketPrice : null,
                suggestedMinimum: null,
              })}
              label="Fixed price"
            />
            <RadioOption
              selected={formState.pricingType === 'donation'}
              onClick={() => onUpdate({ pricingType: 'donation', ticketPrice: null })}
              label="Donation"
            />
          </div>

          {formState.pricingType === 'fixed' && (
            <div className="flex flex-col gap-2 sm:ml-8">
              <label className="type-body-sm text-text-dim">Ticket price</label>
              <div className="relative w-full sm:w-40">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" aria-hidden="true">{currencyPrefix}</span>
                <input
                  type="number"
                  value={formState.ticketPrice ?? ''}
                  onChange={(e) => onUpdate({ ticketPrice: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="input w-full"
                  style={moneyInputStyle}
                />
              </div>
            </div>
          )}

          {formState.pricingType === 'donation' && (
            <div className="flex flex-col gap-2 sm:ml-8">
              <label className="type-body-sm text-text-dim">
                Suggested amount <span className="text-text-faint">(optional)</span>
              </label>
              <div className="relative w-full sm:w-40">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" aria-hidden="true">{currencyPrefix}</span>
                <input
                  type="number"
                  value={formState.suggestedMinimum ?? ''}
                  onChange={(e) => onUpdate({ suggestedMinimum: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="input w-full"
                  style={moneyInputStyle}
                />
              </div>
              <p className="type-caption text-text-faint">
                Attendees can pay any amount, including {formatMoney(0, formState.currency)}
              </p>
            </div>
          )}
        </div>

        {/* Model Selection */}
        <div className="flex flex-col gap-3">
          <h2 className="type-title-card text-text">
            Models <span className="text-text-faint">(optional)</span>
          </h2>

          <div className="mt-2 flex flex-col gap-4">
            {showPayoutModeToggle && (
              <div
                className="inline-flex w-fit overflow-hidden rounded-lg border border-line bg-panel"
                role="group"
                aria-label="Payout mode"
              >
                <button
                  type="button"
                  onClick={() => setPayoutMode('even')}
                  className={`px-3 py-1.5 type-caption transition-colors ${
                    isEvenSplit
                      ? 'bg-text text-bg'
                      : 'text-text-dim hover:bg-panel2 hover:text-text'
                  }`}
                  aria-pressed={isEvenSplit}
                >
                  Even split
                </button>
                <button
                  type="button"
                  onClick={() => setPayoutMode('manual')}
                  className={`px-3 py-1.5 type-caption transition-colors ${
                    !isEvenSplit
                      ? 'bg-text text-bg'
                      : 'text-text-dim hover:bg-panel2 hover:text-text'
                  }`}
                  aria-pressed={!isEvenSplit}
                >
                  Manual split
                </button>
              </div>
            )}

            {splitChartSegments.length > 0 && (
              <div className="flex flex-col gap-3 rounded-lg border border-line bg-panel px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="h-16 w-16 shrink-0 rounded-full border border-line-soft shadow-sm"
                    role="img"
                    aria-label={splitChartAccessibleLabel}
                    data-testid="model-revenue-split-chart"
                    style={{ background: `conic-gradient(${splitChartGradient})` }}
                  />
                  <div className="type-kicker text-text-faint">Revenue split</div>
                </div>
                <div className="grid gap-1.5 sm:min-w-52">
                  {splitChartSegments.map((segment) => (
                    <div key={segment.key} className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/10"
                          style={{ backgroundColor: segment.color }}
                          aria-hidden="true"
                        />
                        <span className="type-caption truncate text-text-dim">{segment.label}</span>
                      </div>
                      <span className="type-caption text-text">{formatSplitPercent(segment.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

              <div className="overflow-visible rounded-lg border border-line bg-panel">
                <div className={`grid gap-3 border-b border-line-soft px-3 py-2 ${
                  showPaidModelTerms
                    ? 'grid-cols-[minmax(0,1fr)_5.5rem_2rem] sm:grid-cols-[minmax(0,1fr)_7rem_10rem_8rem_2rem]'
                    : 'grid-cols-[minmax(0,1fr)_2rem] sm:grid-cols-[minmax(0,1fr)_8rem_2rem]'
                }`}>
                  <div className="type-kicker text-text-faint">Model</div>
                  {showPaidModelTerms && (
                    <>
                      <div className="type-kicker text-text-faint text-right">Split</div>
                      <div className="type-kicker text-text-faint hidden sm:block">Guarantee</div>
                    </>
                  )}
                  <div className="type-kicker text-text-faint hidden sm:block">Invite status</div>
                  <div aria-hidden="true" />
                </div>

                {formState.selectedModels.map((model, index) => {
                  const invite = findInviteForModel(model);
                  const rsvpStatus = invite?.rsvpStatus ?? model.rsvpStatus;
                  const inviteHasBeenSent = hasModelInviteBeenSent(model, invite);
                  const isOrganizerModel = model.source === 'organizer_self';
                  const isJobApplicant = isJobApplicantRow(model);
                  const splitValue = isOrganizerModel
                    ? organizerRemainder
                    : clampPercent(model.splitPercent);
                  const maxSplit = isOrganizerModel ? 100 : getMaxSplitForModel(model);
                  const statusText = isOrganizerModel
                    ? 'Organizer remainder'
                    : isJobApplicant
                      ? 'Job ad'
                      : inviteHasBeenSent
                        ? rsvpStatus === 'confirmed'
                          ? 'Confirmed'
                          : rsvpStatus === 'declined'
                            ? 'Declined'
                            : 'Invitation pending'
                        : 'Save to invite';

                  return (
                    <div
                      key={`${model.id ?? model.source ?? 'invite'}:${model.profileId ?? model.email ?? `${model.displayName}:${index}`}:terms`}
                      className={`grid gap-3 border-b border-line-soft px-3 py-3 last:border-b-0 sm:items-center ${
                        showPaidModelTerms
                          ? 'grid-cols-[minmax(0,1fr)_5.5rem_2rem] sm:grid-cols-[minmax(0,1fr)_7rem_10rem_8rem_2rem]'
                          : 'grid-cols-[minmax(0,1fr)_2rem] sm:grid-cols-[minmax(0,1fr)_8rem_2rem]'
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="type-body-sm text-text truncate">{model.displayName}</div>
                        <div className="type-caption text-text-faint sm:hidden">
                          {statusText}
                        </div>
                      </div>

                      {showPaidModelTerms && (
                        <>
                          {isEvenSplit ? (
                            <div className="flex h-10 items-center justify-end rounded-md border border-line-soft bg-surface px-3 text-right">
                              <span className="type-caption text-text-dim">
                                {organizerIsOnlyModel && isOrganizerModel ? '100%' : 'Even share'}
                              </span>
                            </div>
                          ) : (
                            <label className="relative">
                              <input
                                type="number"
                                min="0"
                                max={maxSplit}
                                value={splitValue}
                                onChange={(e) => updateSelectedModelTerms(model, { splitPercent: e.target.value })}
                                readOnly={isOrganizerModel}
                                disabled={isOrganizerModel}
                                className="input h-10 w-full pr-8 text-right disabled:opacity-100"
                              />
                              <span className="type-caption absolute right-3 top-1/2 -translate-y-1/2 text-text-dim">%</span>
                            </label>
                          )}

                          {model.source !== 'organizer_self' ? (
                            <label className="relative col-span-2 sm:col-span-1">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" aria-hidden="true">{currencyPrefix}</span>
                              <input
                                type="number"
                                value={model.guarantee ?? ''}
                                onChange={(e) => updateSelectedModelTerms(model, { guarantee: e.target.value ? parseFloat(e.target.value) : null })}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                                className="input h-10 w-full"
                                style={moneyInputStyle}
                              />
                            </label>
                          ) : (
                            <div className="hidden sm:block" />
                          )}
                        </>
                      )}

                      <div className="hidden sm:block">
                        {model.source === 'organizer_self' ? (
                          <span className="type-caption inline-flex items-center px-2 py-0.5 rounded-full bg-ok/10 text-ok">Organizer</span>
                        ) : isJobApplicant ? (
                          <span className="type-caption inline-flex items-center px-2 py-0.5 rounded-full bg-accent/10 text-accent">Job ad</span>
                        ) : isLoadingInvite ? (
                          <span className="type-caption text-text-dim">Loading</span>
                        ) : inviteHasBeenSent ? (
                          <span className={`type-caption inline-flex items-center px-2 py-0.5 rounded-full ${
                            rsvpStatus === 'confirmed' ? 'bg-ok/10 text-ok' :
                            rsvpStatus === 'declined' ? 'bg-warn/10 text-warn' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {rsvpStatus === 'confirmed' && 'Confirmed'}
                            {rsvpStatus === 'declined' && 'Declined'}
                            {(!rsvpStatus || rsvpStatus === 'pending') && 'Pending'}
                          </span>
                        ) : (
                          <span className="type-caption text-text-dim">Save to invite</span>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => handleClearSelectedModel(model)}
                        disabled={isWithdrawing}
                        aria-label={invite ? 'Withdraw model invitation' : isJobApplicant ? 'Remove job ad row' : 'Remove selected model'}
                        title={invite ? 'Withdraw invitation' : isJobApplicant ? 'Remove job ad row' : 'Remove selected model'}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-text-dim hover:text-text hover:bg-surface transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isWithdrawing ? (
                          <span className="w-3.5 h-3.5 border-2 border-text-dim/30 border-t-text-dim rounded-full animate-spin" />
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18" />
                            <path d="M6 6l12 12" />
                          </svg>
                        )}
                      </button>
                    </div>
                  );
                })}

                <div className="border-t border-line-soft px-3 py-3">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Add a model by name or email address..."
                      className="input w-full"
                    />
                    {isSearching && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <span className="w-4 h-4 border-2 border-text-dim/30 border-t-text-dim rounded-full animate-spin block" />
                      </div>
                    )}
                    {(searchResults.length > 0 || (searchComplete && isValidEmail(searchQuery) && !isSearchingOwnEmail)) && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-panel border border-line rounded-xl shadow-lg max-h-72 overflow-auto z-10">
                        {searchResults.map((profile) => (
                          <button
                            key={profile.id}
                            onClick={() => handleSelectModel(profile)}
                            className="w-full flex items-center gap-3 p-3 hover:bg-surface transition-colors text-left"
                          >
                            {profile.photos?.[0]?.url ? (
                              <img
                                src={profile.photos[0].url}
                                alt={profile.displayName}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-surface flex items-center justify-center text-text-dim">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                  <circle cx="12" cy="7" r="4" />
                                </svg>
                              </div>
                            )}
                            <div>
                              <div className="type-body-sm text-text">{profile.displayName}</div>
                              {(profile.username || profile.country) && (
                                <div className="type-caption text-text-dim">
                                  {profile.username ? `@${profile.username}` : profile.country}
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                        {searchComplete && isValidEmail(searchQuery) && !isSearchingOwnEmail && (
                          <>
                            {searchResults.length > 0 && (
                              <div className="border-t border-line mx-3" />
                            )}
                            <button
                              onClick={() => handleInviteByEmail(searchQuery)}
                              className="w-full flex items-center gap-3 p-3 hover:bg-accent/5 transition-colors text-left"
                            >
                              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center text-accent">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="2" y="4" width="20" height="16" rx="2" />
                                  <path d="M22 7l-10 6L2 7" />
                                </svg>
                              </div>
                              <div>
                                <div className="type-body-sm text-accent">Invite by email</div>
                                <div className="type-caption text-text-dim">Send invitation to {searchQuery}</div>
                              </div>
                            </button>
                          </>
                        )}
                      </div>
                    )}
                    {searchQuery && !isSearching && searchComplete && searchResults.length === 0 && !isValidEmail(searchQuery) && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-panel border border-line rounded-xl shadow-lg p-4 z-10">
                        <p className="type-body-sm text-text-dim">
                          No models found. Search by model name or enter an email address to invite someone directly.
                        </p>
                      </div>
                    )}
                    {isSearchingOwnEmail && searchComplete && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-panel border border-line rounded-xl shadow-lg p-4 z-10">
                        <p className="type-body-sm text-text-dim">
                          To model for your own session, use Add me as model instead.
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {!selectedOrganizerModel && (
                      <button
                        type="button"
                        onClick={addOrganizerAsModel}
                        className="btn btn-sm"
                      >
                        Add me as model
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={addJobApplicantRow}
                      className="btn btn-sm"
                    >
                      Post an ad
                    </button>
                  </div>
                </div>
              </div>

              {!selectedOrganizerModel && showPaidModelTerms && (
                <div className="overflow-hidden rounded-lg border border-line bg-panel">
                  <div className="grid grid-cols-[minmax(0,1fr)_5.5rem] gap-3 border-b border-line-soft px-3 py-2 sm:grid-cols-[minmax(0,1fr)_7rem]">
                    <div className="type-kicker text-text-faint">Organizer</div>
                    <div className="type-kicker text-text-faint text-right">Split</div>
                  </div>
                  <div className="grid grid-cols-[minmax(0,1fr)_5.5rem] gap-3 bg-surface/50 px-3 py-3 sm:grid-cols-[minmax(0,1fr)_7rem] sm:items-center">
                    <div className="min-w-0">
                      <div className="type-body-sm text-text truncate">You</div>
                      <div className="type-caption text-text-faint">Organizer remainder</div>
                    </div>
                    {isEvenSplit ? (
                      <div className="flex h-10 items-center justify-end rounded-md border border-line-soft bg-surface px-3 text-right">
                        <span className="type-caption text-text-dim">Even share</span>
                      </div>
                    ) : (
                      <label className="relative">
                        <input
                          type="number"
                          value={organizerRemainder}
                          readOnly
                          disabled
                          className="input h-10 w-full pr-8 text-right disabled:opacity-100"
                        />
                        <span className="type-caption absolute right-3 top-1/2 -translate-y-1/2 text-text-dim">%</span>
                      </label>
                    )}
                  </div>
                </div>
              )}

              {showPaidModelTerms && (visibleRevenueSplitTotal !== 100 || showMetricSystemSplitApology) && (
                <div className={`type-caption ${visibleRevenueSplitTotal === 100 ? 'text-text-faint' : 'text-warn'}`}>
                  {visibleRevenueSplitTotal !== 100 && `Allocated: ${visibleRevenueSplitTotal}%. Organizer remainder: ${organizerRemainder}%.`}
                  {showMetricSystemSplitApology && 'The metric system says sorry.'}
                </div>
              )}

              {hasGuarantee && (
                <p className="type-caption text-text-faint">
                  If ticket sales don't cover the guarantees, your card will be charged after the event.
                </p>
              )}
              {isPublished && hasGuarantee && (
                <p className="type-caption text-amber-600">
                  Changing the guarantee will adjust the hold on your card when you save.
                </p>
              )}

            </div>

          {modelError && modelError !== JOB_AD_TEXT_ERROR && (
            <p className="type-validation-message sm:ml-8 sm:max-w-[calc(100%-2rem)]" role="alert">
              {modelError}
            </p>
          )}
        </div>

        {/* Payout Simulator (when fixed pricing and has a model) */}
        {formState.pricingType === 'fixed' && ticketPrice > 0 && formState.selectedModels.length > 0 && (
          <div className="flex flex-col gap-4 p-5 rounded-xl bg-panel border border-line mt-2">
            <div className="flex items-center justify-between">
              <h2 className="type-title-card text-text">Payout Simulator</h2>
              <div className="type-body-sm text-text-dim">
                {clampedSimulatorValue} ticket{clampedSimulatorValue !== 1 ? 's' : ''} sold
              </div>
            </div>

            <input
              type="range"
              min="0"
              max={maxTickets}
              value={clampedSimulatorValue}
              onChange={(e) => setSimulatorValue(parseInt(e.target.value, 10))}
              className="w-full h-2 bg-gray-300 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-accent"
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="flex flex-col gap-1 p-3 rounded-lg bg-surface">
                <span className="type-kicker text-text-faint">Gross Revenue</span>
                <span className="type-title-card text-text">${payouts.grossRevenue.toFixed(2)}</span>
              </div>
              <div className="flex flex-col gap-1 p-3 rounded-lg bg-surface">
                <span className="type-kicker text-text-faint">Platform Fee ({platformFeePercent}%)</span>
                <span className="type-title-card text-warn">-${payouts.platformFee.toFixed(2)}</span>
              </div>
              <div className="flex flex-col gap-1 p-3 rounded-lg bg-surface">
                <span className="type-kicker text-text-faint">Net to Split</span>
                <span className="type-title-card text-text">${payouts.netRevenue.toFixed(2)}</span>
              </div>
            </div>

            <div className="border-t border-line-soft pt-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {payouts.payoutRows.map((row) => {
                  const isOrganizerRow = row.role.includes('Organizer');

                  return (
                    <div
                      key={row.key}
                      className={`flex flex-col gap-1 p-3 rounded-lg border ${
                        isOrganizerRow
                          ? 'bg-accent/10 border-accent/20'
                          : 'bg-tag/10 border-tag/20'
                      }`}
                    >
                      <span className={`type-kicker ${isOrganizerRow ? 'text-accent' : 'text-tag'}`}>
                        {row.label === 'You' ? 'You Receive' : `${row.label} Receives`}
                      </span>
                      <span className={`type-title-card ${isOrganizerRow ? 'text-accent' : 'text-tag'}`}>
                        {formatMoneyMajor(row.payout, formState.currency)}
                      </span>
                      <span className="type-caption text-text-dim">
                        {row.role} · {row.splitLabel}
                      </span>
                      {row.guarantee > 0 && (
                        <span className="type-caption text-text-dim">
                          Guarantee {formatMoneyMajor(row.guarantee, formState.currency)}
                          {row.guaranteeTopUp > 0 && ` · top-up ${formatMoneyMajor(row.guaranteeTopUp, formState.currency)}`}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              {payouts.guaranteeCharged > 0 && (
                <p className="type-caption text-warn mt-3 flex items-center gap-1.5">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  Your card would be charged {formatMoneyMajor(payouts.guaranteeCharged, formState.currency)} to meet the model guarantees.
                </p>
              )}
            </div>
          </div>
        )}

        {(hasKnownModelInviteRows || hasJobAdRows) && (
          <div className="flex flex-col gap-4">
            {hasKnownModelInviteRows && (
              <div className="flex flex-col gap-2">
                <label className="type-body-sm text-text-dim">
                  Message for models <span className="text-text-faint">(optional)</span>
                </label>
                <textarea
                  value={formState.modelExpectations}
                  onChange={(e) => onUpdate({ modelExpectations: e.target.value })}
                  placeholder="Any special requests or information for the model..."
                  className="input min-h-[80px] resize-none pt-3"
                  rows={3}
                />
                <p className="type-caption text-text-faint">
                  This will be included in invitation emails.
                </p>
              </div>
            )}

            {hasJobAdRows && (
              <div className="flex flex-col gap-2">
                <label className="type-body-sm text-text-dim">
                  Job text <span className="text-text-faint">(required to publish ad)</span>
                </label>
                <textarea
                  value={formState.modelAdText}
                  onChange={(e) => {
                    onUpdate({ modelAdText: e.target.value });
                    if (modelError === JOB_AD_TEXT_ERROR && e.target.value.trim()) {
                      setModelError(null);
                    }
                  }}
                  placeholder="Pose type, experience level, setup, lighting, wardrobe, or anything else applicants should know..."
                  className="input min-h-[104px] resize-none pt-3"
                  rows={4}
                  maxLength={2000}
                />
                {modelError === JOB_AD_TEXT_ERROR && (
                  <p className="type-validation-message" role="alert">
                    {modelError}
                  </p>
                )}
                <p className="type-caption text-text-faint">
                  This will be shown on the job board.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Model Guarantee Info */}
        {hasGuarantee && formState.pricingType !== 'free' && formState.selectedModels.length > 0 && (
          <div className="flex flex-col gap-3 p-4 rounded-xl bg-panel border border-line">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-tag/10 flex items-center justify-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-tag">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <div>
                <div className="type-body-sm text-text">Model Guarantees</div>
                <div className="type-body-sm text-text-dim">${activeGuaranteeAmount.toFixed(2)}</div>
              </div>
            </div>

            {/* Payment Card */}
            {!isCheckingPayment && (
              <div className="flex flex-col gap-3 p-3 rounded-lg bg-surface border border-line-soft sm:flex-row sm:items-center sm:justify-between">
                {paymentMethod ? (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-6 rounded bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
                        <span className="type-kicker text-white">
                          {paymentMethod.brand}
                        </span>
                      </div>
                      <div>
                        <div className="type-body-sm text-text">•••• {paymentMethod.last4}</div>
                        <div className="type-caption text-text-faint">
                          Expires {paymentMethod.expMonth}/{paymentMethod.expYear}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowCardModal(true)}
                      className="type-body-sm text-accent hover:text-accent-deep"
                    >
                      Change
                    </button>
                  </>
                ) : (
                  <>
                    <div className="type-body-sm text-text-dim">No payment method on file</div>
                    <button
                      onClick={() => setShowCardModal(true)}
                      className="type-body-sm text-accent hover:text-accent-deep"
                    >
                      Add Card
                    </button>
                  </>
                )}
              </div>
            )}

            <p className="type-caption text-text-faint">
              {isPublished
                ? 'Your card has been authorized. The hold will be released after the event if ticket sales cover the guarantee.'
                : 'When you publish, your card will be authorized for this amount. It will only be charged if ticket sales don\'t cover the guarantee.'}
            </p>
          </div>
        )}

        {/* Publish Status - only show when already published */}
        {isPublished && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-green-50 border border-green-200">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-600">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <div className="type-body-sm text-green-900">Event is live</div>
          </div>
        )}

        {/* Error Message */}
        {publishError && (
          <div className="type-validation-message" role="alert">
            {publishError}
          </div>
        )}

        {/* Buttons */}
        <div className="flex flex-col gap-2.5 mt-auto pt-[18px] border-t border-line-soft sm:flex-row sm:items-center">
          <button onClick={onBack} className="btn w-full sm:w-auto">
            ← Back
          </button>
          <div className="hidden flex-1 sm:block" />
          <button
            onClick={handleContinue}
            disabled={isSubmitting || !roomCode || isLoadingInvite}
            className="btn btn-accent btn-lg w-full sm:w-auto"
          >
            {isSubmitting ? 'Saving...' :
              isLoadingInvite ? 'Checking invitation...' :
              // New invitation about to be sent
              hasPendingModelInvitation ? 'Invite and Continue' :
              formState.selectedModels.some(isJobApplicantRow) ? 'Save Model Details and Continue' :
              // Otherwise standard behavior
              (isPublished ? 'Save Changes' : 'Next')}
          </button>
        </div>
      </div>

      {/* Card Modal */}
      <GuaranteeCardModal
        isOpen={showCardModal}
        onClose={() => setShowCardModal(false)}
        onSuccess={handleCardSuccess}
        guaranteeAmount={activeGuaranteeAmount}
        getToken={getToken}
      />
    </div>
  );
}

function RadioOption({
  selected,
  onClick,
  label,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <div onClick={onClick} className="flex items-center gap-3 cursor-pointer group">
      <div
        className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-colors ${
          selected ? 'border-accent' : 'border-line group-hover:border-text/30'
        }`}
      >
        {selected && <div className="w-2.5 h-2.5 rounded-full bg-accent" />}
      </div>
      <div className="type-body-sm text-text">{label}</div>
    </div>
  );
}
